import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { ArrowRight } from "@/components/ui/Button";
import { useForm, useSettings } from "@/content-context";
import { interestOptions as fallbackInterests, seedForms } from "@/data/forms";
import type { FormFieldDoc } from "@/content";

export const interestOptions = fallbackInterests;

type FieldErrors = Record<string, string>;

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "sent" }
  | { kind: "unconfigured" }
  | { kind: "error"; message: string };

/**
 * Enquiry form.
 *
 * The fields come from the CMS now, and everything that made this component
 * trustworthy is unchanged.
 *
 * Every field has a visible <label>; placeholders are never the only label.
 * Validation runs client-side for immediate feedback and again on the server,
 * where it is derived from the same configuration rather than replaced by it.
 * If no delivery channel is configured the form reports that honestly: it never
 * shows a success state for a message that was not sent. That is the oldest
 * promise on this site and a form builder does not get to weaken it, which is
 * why name, email, enquiry type, message and consent are locked: the API
 * refuses to let a super admin remove them.
 *
 * `?interest=` preselects the enquiry type. That is how "Volunteer with us"
 * opens the contact form with Volunteering already chosen without inventing an
 * application process, and it is read from the URL rather than passed through
 * state so the link works from anywhere, including from an email.
 */
export function EnquiryForm({
  defaultInterest = "general",
  heading = "Send an enquiry",
  formKey = "enquiry",
}: {
  defaultInterest?: string;
  heading?: string;
  /** Which configured form to render. "enquiry" unless a page has its own. */
  formKey?: string;
}) {
  const uid = useId();
  const settings = useSettings();
  const configured = useForm(formKey);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const formRef = useRef<HTMLFormElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /** The committed default, used until the CMS has been seeded or if it is emptied. */
  const fallback = useMemo(
    () => seedForms.find((f) => f.key === formKey) ?? seedForms[0],
    [formKey],
  );

  const fields: FormFieldDoc[] = useMemo(() => {
    const source = configured?.fields?.length ? configured.fields : (fallback.fields as FormFieldDoc[]);
    return source
      .filter((field) => field.enabled !== false)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [configured, fallback]);

  const interestField = fields.find((f) => f.name === "interest");
  const interestValues = (interestField?.options ?? fallbackInterests).map((o) => o.value);

  /*
   * The enquiry type, resolved once.
   *
   * A value in the URL wins, but only if it is one the form actually offers.
   * Accepting anything would let a link put arbitrary text into the enquiry
   * that reaches staff, and the select would silently fall back to its first
   * option while the visitor believed they had chosen something.
   */
  const requested = searchParams.get("interest");
  const initialInterest =
    requested && interestValues.includes(requested)
      ? requested
      : interestValues.includes(defaultInterest)
        ? defaultInterest
        : (interestValues[0] ?? "general");

  const [interest, setInterest] = useState(initialInterest);
  useEffect(() => setInterest(initialInterest), [initialInterest]);

  /*
   * The no-script path.
   *
   * A native form POST is answered with a redirect carrying only a status word,
   * never anything the visitor typed. If the script is running by the time the
   * page comes back, that word is turned into the same message a fetch()
   * submission would show, and then removed from the URL so a refresh or a
   * shared link does not repeat it.
   */
  useEffect(() => {
    const outcome = searchParams.get("enquiry");
    if (!outcome) return;

    const mapped: Record<string, Status> = {
      sent: { kind: "sent" },
      unconfigured: { kind: "unconfigured" },
      failed: { kind: "error", message: "The message could not be sent. Please try again." },
      invalid: {
        kind: "error",
        message: "Some required details were missing. Please check the form and try again.",
      },
    };
    const next = mapped[outcome];
    if (!next) return;

    setStatus(next);
    const params = new URLSearchParams(searchParams);
    params.delete("enquiry");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  /*
   * Move focus to the outcome.
   *
   * Without this, a submission announces itself to a screen reader through the
   * live region but leaves focus on a button that now says something else, and
   * a keyboard user has no way to reach the message except by hunting for it.
   */
  useEffect(() => {
    if (status.kind === "idle" || status.kind === "submitting") return;
    statusRef.current?.focus();
  }, [status]);

  const fieldId = (name: string) => `${uid}-${name}`;
  const errorId = (name: string) => `${uid}-${name}-error`;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status.kind === "submitting") return;
    const form = event.currentTarget;
    const data = new FormData(form);

    const values: Record<string, string | boolean> = {};
    for (const field of fields) {
      values[field.name] =
        field.type === "checkbox" ? data.get(field.name) === "on" : String(data.get(field.name) ?? "").trim();
    }
    // Honeypot: a real person never fills a hidden field.
    values.website = String(data.get("website") ?? "");

    const nextErrors: FieldErrors = {};
    for (const field of fields) {
      const value = values[field.name];
      if (field.required) {
        if (field.type === "checkbox" && value !== true) {
          nextErrors[field.name] = "Please confirm this to continue.";
          continue;
        }
        if (typeof value === "string" && value.length === 0) {
          nextErrors[field.name] = `${field.label} is required.`;
          continue;
        }
      }
      if (field.type === "email" && typeof value === "string" && value) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          nextErrors[field.name] = "Enter a valid email address so we can reply.";
        }
      }
    }
    // The two rules the API enforces regardless of how the form is configured.
    if (typeof values.name === "string" && values.name.length > 0 && values.name.length < 2) {
      nextErrors.name = "Enter your full name.";
    }
    if (typeof values.message === "string" && values.message.length > 0 && values.message.length < 20) {
      nextErrors.message = "Please give us at least a sentence or two of context.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstKey = Object.keys(nextErrors)[0];
      document.getElementById(fieldId(firstKey))?.focus();
      return;
    }

    setStatus({ kind: "submitting" });

    try {
      const response = await fetch("/api/enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (response.status === 503) {
        setStatus({ kind: "unconfigured" });
        return;
      }
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        setStatus({
          kind: "error",
          message: body?.error ?? "The message could not be sent. Please try again.",
        });
        return;
      }

      form.reset();
      setStatus({ kind: "sent" });
    } catch {
      setStatus({
        kind: "error",
        message: "We could not reach the server. Check your connection and try again.",
      });
    }
  }

  const inputClass =
    "mt-2 block w-full min-h-11 rounded-[10px] border border-line-strong bg-white px-3.5 py-2.5 text-ink transition-colors focus:border-navy";
  const labelClass = "block text-[0.9375rem] font-semibold text-ink";
  const hintClass = "mt-1 block text-[0.8125rem] font-normal text-muted";

  /** A field that spans both columns: long text, the enquiry type, consent. */
  const isWide = (field: FormFieldDoc) =>
    field.type === "textarea" || field.type === "checkbox" || field.name === "interest" || field.name === "organisation";

  return (
    /*
      method and action are not decoration. A form with neither renders as
      method="get", so if the script fails to load or hydrate, the browser
      submits to the current URL and puts the visitor's name, email address and
      message into the query string, where they land in history, in the referrer
      and in every access log along the way.
    */
    <form
      ref={formRef}
      method="post"
      action="/api/enquiry"
      onSubmit={handleSubmit}
      noValidate
      className="rounded-[16px] border border-line bg-white p-6 md:p-8"
    >
      <h2 style={{ fontSize: "var(--step-h3)" }}>{heading}</h2>
      <p className="mt-2 text-[0.9375rem] text-muted">
        Fields marked with an asterisk (<span aria-hidden="true">*</span>
        <span className="sr-only">required</span>) are required.
      </p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        {fields.map((field) => {
          const error = errors[field.name];
          const described = error ? errorId(field.name) : undefined;

          if (field.type === "checkbox") {
            return (
              <div key={field.name} className="sm:col-span-2">
                <div className="flex items-start gap-3">
                  <input
                    id={fieldId(field.name)}
                    name={field.name}
                    type="checkbox"
                    required={field.required}
                    aria-invalid={Boolean(error)}
                    aria-describedby={described}
                    className="mt-1 h-5 w-5 shrink-0 rounded border-line-strong accent-[#14386f]"
                  />
                  <label htmlFor={fieldId(field.name)} className="text-[0.9375rem] text-ink">
                    {field.label} {field.required ? <span aria-hidden="true">*</span> : null}{" "}
                    {field.name === "consent" ? (
                      <Link to="/privacy" className="link-underline font-semibold text-navy">
                        How your details are handled
                      </Link>
                    ) : null}
                  </label>
                </div>
                {error ? (
                  <p id={described} className="mt-1.5 text-[0.875rem] text-[#a3261f]">
                    {error}
                  </p>
                ) : null}
              </div>
            );
          }

          return (
            <div key={field.name} className={isWide(field) ? "sm:col-span-2" : "sm:col-span-1"}>
              <label htmlFor={fieldId(field.name)} className={labelClass}>
                {field.label} {field.required ? <span aria-hidden="true">*</span> : null}
                {field.help ? <span className={hintClass}>{field.help}</span> : null}
              </label>

              {field.type === "select" ? (
                <select
                  id={fieldId(field.name)}
                  name={field.name}
                  required={field.required}
                  value={field.name === "interest" ? interest : undefined}
                  defaultValue={field.name === "interest" ? undefined : ""}
                  onChange={
                    field.name === "interest" ? (event) => setInterest(event.target.value) : undefined
                  }
                  aria-invalid={Boolean(error)}
                  aria-describedby={described}
                  /* Same chevron as every other select on the site. */
                  className={`${inputClass} select-field`}
                >
                  {(field.options ?? []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === "textarea" ? (
                <textarea
                  id={fieldId(field.name)}
                  name={field.name}
                  rows={6}
                  required={field.required}
                  maxLength={field.maxLength}
                  aria-invalid={Boolean(error)}
                  aria-describedby={described}
                  className={`${inputClass} resize-y`}
                />
              ) : (
                <input
                  id={fieldId(field.name)}
                  name={field.name}
                  type={field.type}
                  autoComplete={autoCompleteFor(field.name)}
                  required={field.required}
                  maxLength={field.maxLength}
                  aria-invalid={Boolean(error)}
                  aria-describedby={described}
                  className={inputClass}
                />
              )}

              {error ? (
                <p id={described} className="mt-1.5 text-[0.875rem] text-[#a3261f]">
                  {error}
                </p>
              ) : null}
            </div>
          );
        })}

        {/* Honeypot, visually and programmatically hidden from real users. */}
        <div aria-hidden="true" className="hidden">
          <label htmlFor={fieldId("website")}>Leave this field empty</label>
          <input id={fieldId("website")} name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>
      </div>

      <div className="mt-7">
        <button
          type="submit"
          disabled={status.kind === "submitting"}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-[10px] bg-navy px-6 py-3 text-[0.9375rem] font-semibold text-white transition-colors hover:bg-midnight disabled:opacity-70"
        >
          {status.kind === "submitting" ? "Sending…" : (configured?.submitLabel ?? fallback.submitLabel)}
          <ArrowRight />
        </button>
      </div>

      <div ref={statusRef} tabIndex={-1} aria-live="polite" className="mt-6 outline-none empty:mt-0">
        {status.kind === "sent" ? (
          <div className="rounded-[12px] border border-line bg-mist p-5">
            <p className="font-semibold text-navy">Your enquiry has been sent.</p>
            <p className="mt-2 text-[0.9375rem] text-muted">
              {configured?.successNote ?? fallback.successNote}
            </p>
          </div>
        ) : null}

        {status.kind === "unconfigured" ? (
          <div className="notice notice-amber">
            <p className="font-semibold text-ink">This form is not connected yet.</p>
            <p className="mt-2 text-[0.9375rem] text-muted">
              Your message was <strong>not</strong> sent. The enquiry inbox has not been configured
              for this site yet. Please reach HISCUS directly in the meantime:
            </p>
            <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[0.9375rem]">
              {settings.email.confirmed && settings.email.value ? (
                <li>
                  <a
                    href={`mailto:${settings.email.value}`}
                    className="font-semibold text-navy underline underline-offset-4"
                  >
                    {settings.email.value}
                  </a>
                </li>
              ) : null}
              {settings.socialLinks.map((social) => (
                <li key={social.href}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-navy underline underline-offset-4"
                  >
                    {social.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {status.kind === "error" ? (
          <div className="notice notice-rose">
            <p className="font-semibold text-ink">Your message was not sent.</p>
            <p className="mt-2 text-[0.9375rem] text-muted">{status.message}</p>
          </div>
        ) : null}
      </div>
    </form>
  );
}

/** Autofill hints for the built-in fields, so a browser can help rather than guess. */
function autoCompleteFor(name: string) {
  switch (name) {
    case "name":
      return "name";
    case "email":
      return "email";
    case "phone":
      return "tel";
    case "country":
      return "country-name";
    case "organisation":
      return "organization";
    default:
      return "off";
  }
}
